import type { AdminBasemap, BasemapSourceType, WmtsBasemapSettings } from '../admin/types';
import { ApiError } from './api';
import type { StyleRule, StyleSymbolizer } from './layersApi';

const MAPS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

/** Everything here is unauthenticated on purpose — this backs the /embed/:token
 * public map viewer, which by definition runs with no logged-in user and no
 * workspace context. Never add a Bearer token here (there wouldn't be one to
 * add, and it would be a sign something's wired to the wrong client). */
async function publicFetch<T>(path: string): Promise<T> {
  const response = await fetch(`${MAPS_API_BASE}${path}`);
  if (!response.ok) {
    let message = 'Не удалось загрузить карту';
    try {
      const body = await response.json();
      if (typeof body.detail === 'string') message = body.detail;
    } catch {
      // тело ответа не JSON — оставляем сообщение по умолчанию
    }
    throw new ApiError(response.status, message);
  }
  return response.json();
}

export type PublicEmbedConfig = {
  show_legend: boolean;
  show_basemap_switcher: boolean;
  width: number;
  height: number;
};

export type PublicMapDto = {
  title: string;
  default_basemap_name: string;
  center_lat: number;
  center_lon: number;
  zoom: number;
  zoom_min: number;
  zoom_max: number;
  min_lat: number;
  min_lon: number;
  max_lat: number;
  max_lon: number;
  restrict_pan_to_extent: boolean;
  embed_config: PublicEmbedConfig;
};

export type PublicBasemapDto = {
  id: string;
  name: string;
  source_type: string;
  url_template: string;
  min_zoom: number;
  max_zoom: number;
  tile_size: number;
  crs: string;
  attribution: string;
  settings: Record<string, unknown>;
};

export type PublicLayerDto = {
  id: string;
  name: string;
  geometry_type: string;
  style_color: string | null;
  style_opacity: number | null;
  style_stroke_width: number | null;
  style_marker_mode: string | null;
  style_marker_icon_file_id: string | null;
  style_type: 'simple' | 'rule_based';
  style_rules: StyleRule[];
  style_fallback: StyleSymbolizer | null;
};

export type PublicFeatureCollection = {
  type: 'FeatureCollection';
  features: {
    type: 'Feature';
    id: string;
    geometry: { type: string; coordinates?: unknown };
    properties: Record<string, unknown>;
  }[];
};

const sourceTypeFromDto: Record<string, BasemapSourceType> = {
  xyz: 'XYZ Tiles',
  wmts: 'WMTS',
  tms: 'TMS',
  mbtiles: 'MBTiles',
};

/** Same mapping as basemapsApi.ts's fromDto — duplicated rather than shared
 * because that function isn't exported and the two DTOs (authenticated vs.
 * public) intentionally carry different fields. */
export function toAdminBasemap(dto: PublicBasemapDto): AdminBasemap {
  return {
    id: dto.id,
    name: dto.name,
    sourceType: sourceTypeFromDto[dto.source_type] ?? 'XYZ Tiles',
    urlTemplate: dto.url_template,
    minZoom: dto.min_zoom,
    maxZoom: dto.max_zoom,
    tileSize: dto.tile_size === 512 ? 512 : 256,
    crs: dto.crs,
    attribution: dto.attribution,
    enabled: true,
    isDefault: false,
    settings: dto.settings && 'matrix_ids' in dto.settings ? (dto.settings as WmtsBasemapSettings) : undefined,
  };
}

export const publicApi = {
  getMap: (token: string) => publicFetch<PublicMapDto>(`/public/${token}`),
  listBasemaps: (token: string) => publicFetch<PublicBasemapDto[]>(`/public/${token}/basemaps`),
  listLayers: (token: string) => publicFetch<PublicLayerDto[]>(`/public/${token}/layers`),
  getLayerFeatures: (token: string, layerId: string) =>
    publicFetch<PublicFeatureCollection>(`/public/${token}/layers/${layerId}/features`),
};
